import moment from "moment";

const ZONE_BEFORE_EARLY = Symbol("before-early");
const ZONE_EARLY_GRACE = Symbol("early-grace");
const ZONE_VALID = Symbol("valid");
const ZONE_LATE_GRACE = Symbol("late-grace");
const ZONE_AFTER_LATE = Symbol("after-late");

export const STATUS_INVALID = Object.freeze({ isValid: false, isInGrace: false, isEither: false });
export const STATUS_GRACE = Object.freeze({ isValid: false, isInGrace: true, isEither: true });
export const STATUS_VALID = Object.freeze({ isValid: true, isInGrace: false, isEither: true });

// Keyed by [fromZone][toZone] → { whenFull, whenPartial }.
// "from" always falls at or before "to" in zone order, so only
// forward-reachable "to" zones are defined per "from" zone.
// A missing entry is treated as invalid.
const ZONE_RANGE_STATES = Object.freeze({
	[ZONE_BEFORE_EARLY]: {
		[ZONE_BEFORE_EARLY]: { whenFull: STATUS_INVALID, whenPartial: STATUS_INVALID },
		[ZONE_EARLY_GRACE]:  { whenFull: STATUS_INVALID, whenPartial: STATUS_GRACE },
		[ZONE_VALID]:        { whenFull: STATUS_INVALID, whenPartial: STATUS_VALID },
		[ZONE_LATE_GRACE]:   { whenFull: STATUS_INVALID, whenPartial: STATUS_VALID },
		[ZONE_AFTER_LATE]:   { whenFull: STATUS_INVALID, whenPartial: STATUS_VALID }
	},
	[ZONE_EARLY_GRACE]: {
		[ZONE_EARLY_GRACE]: { whenFull: STATUS_GRACE,   whenPartial: STATUS_GRACE },
		[ZONE_VALID]:       { whenFull: STATUS_GRACE,   whenPartial: STATUS_VALID },
		[ZONE_LATE_GRACE]:  { whenFull: STATUS_GRACE,   whenPartial: STATUS_VALID },
		[ZONE_AFTER_LATE]:  { whenFull: STATUS_INVALID, whenPartial: STATUS_VALID }
	},
	[ZONE_VALID]: {
		[ZONE_VALID]:      { whenFull: STATUS_VALID,   whenPartial: STATUS_VALID },
		[ZONE_LATE_GRACE]: { whenFull: STATUS_GRACE,   whenPartial: STATUS_VALID },
		[ZONE_AFTER_LATE]: { whenFull: STATUS_INVALID, whenPartial: STATUS_VALID }
	},
	[ZONE_LATE_GRACE]: {
		[ZONE_LATE_GRACE]: { whenFull: STATUS_GRACE,   whenPartial: STATUS_GRACE },
		[ZONE_AFTER_LATE]: { whenFull: STATUS_INVALID, whenPartial: STATUS_GRACE }
	},
	[ZONE_AFTER_LATE]: {
		[ZONE_AFTER_LATE]: { whenFull: STATUS_INVALID, whenPartial: STATUS_INVALID }
	}
});

export const effectiveRangesStatus = (ranges, effectiveAsOf) => {
	if(!ranges || !effectiveAsOf)
		return STATUS_INVALID;
	
	if(!Array.isArray(ranges) || !ranges.length)
		return STATUS_INVALID;
	
	let isInGrace = false;
	
	for(const range of ranges) {
		const status = effectiveRangeStatus(range, effectiveAsOf);
		if(status.isValid)
			return status;
		
		isInGrace = isInGrace || status.isInGrace;
	}
	
	return isInGrace ? STATUS_GRACE : STATUS_INVALID;
};

export function effectiveRangeStatus(
	range,
	asOf
) {
	const effectiveRangeStart = moment(range.Start);
	const effectiveRangeEnd = range.End ? moment(range.End) : null;
	const effectiveRangeAsEarlyAs = range.AsEarlyAs ? moment(range.AsEarlyAs) : null;
	const effectiveRangeNoLaterThan = range.NoLaterThan ? moment(range.NoLaterThan) : null;

	const asOfRangeFrom = asOf.fromInclusive;
	const asOfRangeTo = asOf.toExclusive;

	const classify = (point, inclusive) => {
		if(inclusive ? point.isBefore(effectiveRangeStart) : !point.isAfter(effectiveRangeStart)) {
			if(effectiveRangeAsEarlyAs && (inclusive ? !point.isBefore(effectiveRangeAsEarlyAs) : point.isAfter(effectiveRangeAsEarlyAs)))
				return ZONE_EARLY_GRACE;
			if(effectiveRangeAsEarlyAs && (inclusive ? point.isBefore(effectiveRangeAsEarlyAs) : !point.isAfter(effectiveRangeAsEarlyAs)))
				return ZONE_BEFORE_EARLY;
			return ZONE_BEFORE_EARLY;
		}
		if(!effectiveRangeEnd || (inclusive ? point.isBefore(effectiveRangeEnd) : !point.isAfter(effectiveRangeEnd)))
			return ZONE_VALID;
		if(effectiveRangeNoLaterThan && (inclusive ? point.isBefore(effectiveRangeNoLaterThan) : !point.isAfter(effectiveRangeNoLaterThan)))
			return ZONE_LATE_GRACE;
		return ZONE_AFTER_LATE;
	};

	const isPoint = asOfRangeFrom.isSame(asOfRangeTo);
	const fromZone = classify(asOfRangeFrom, true);
	const toZone = isPoint ? fromZone : classify(asOfRangeTo, false);

	const state = ZONE_RANGE_STATES[fromZone]?.[toZone];
	if(!state)
		return STATUS_INVALID;

	return asOf.allowPartial ? state.whenPartial : state.whenFull;
}
